import type { QueryView, ServingHealth, TimelineEntry, TradingHoursAdminModel } from '.';
import { STALE_PIN_SECONDS } from '.';
import {
    closePending,
    initialTradingHoursAdminState,
    openConfirm as openConfirmAction,
    openFreeze as openFreezeAction,
    openUnfreeze as openUnfreezeAction,
    selectVersion as selectVersionAction,
    setDiffDraft as setDiffDraftAction,
    setFreezeOptions as setFreezeOptionsAction,
    setHistoryFilter as setHistoryFilterAction,
    setHolidayDraft as setHolidayDraftAction,
    setFreezeEffectiveId,
    setPendingBusy,
    setPendingReason as setPendingReasonAction,
    setRecordForm as setRecordFormAction,
    setRecordResult,
    setTab as setTabAction,
    setUnfreezePreview,
    setVersionsLimit as setVersionsLimitAction,
    submitDiff,
    submitHolidayLookup,
    tradingHoursAdminReducer,
} from './state';
import type {
    ConfirmableAction,
    DiffSelection,
    HolidayLookup,
    PendingAction,
    RecordBlacklistForm,
    TradingHoursAdminAction,
    TradingHoursAdminState,
    TradingHoursTab,
} from './state';
import { computed, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { QueryObserverOptions } from '@tanstack/query-core';
import { EmptyArray } from '@thinkalpha/common/util/empty.js';
import type {
    ScheduleDiff,
    TradingHoursHistoryView,
    TradingHoursHolidayHistoryView,
    TradingHoursIngestionView,
    TradingHoursRecordBlacklistResult,
    TradingHoursRecordOverrideView,
    TradingHoursRecordOverridesResult,
    TradingHoursScheduleStatusView,
    TradingHoursVersionsView,
} from '@thinkalpha/quant-ws-client/contracts/trading-hours-admin.js';
import { t } from 'i18next';
import { triggerBlobDownload } from 'src/admin/trading/TradingHours/download';
import { parseIdsFromText as parseIds, shortId } from 'src/admin/trading/TradingHours/formatters';
import { log } from 'src/admin/trading/TradingHours/log';
import { inject, injectable } from 'src/features/ioc';
import type { QueryObserverModel } from 'src/models/QueryObserverModel';
import {
    downloadArchive as downloadArchiveRequest,
    freeze,
    getTradingHoursDiffQuery,
    getTradingHoursHistoryQuery,
    getTradingHoursHolidayHistoryQuery,
    getTradingHoursOverridesQuery,
    getTradingHoursStatusQuery,
    getTradingHoursVersionsQuery,
    invalidateTradingHoursAdminQueries,
    releaseRecordOverride,
    setRecordsBlacklist,
    setVersionsBlacklist,
    unfreeze,
} from 'src/queries/tradingHoursAdmin';
import type { HolidayHistoryParams } from 'src/queries/tradingHoursAdmin';
import type { ReactBindings } from 'src/types/bindings';

/** FinIDs carry a dot (`US.NYSE`); anything else is taken as a MIC. */
export const holidayLookupParams = (lookup: HolidayLookup): HolidayHistoryParams =>
    lookup.market.includes('.')
        ? { date: lookup.date, finId: lookup.market }
        : { date: lookup.date, mic: lookup.market };

const UNREACHABLE_STATUSES = new Set([502, 503, 504]);

/** The worker's refusal text when present (`{ error }` body), else the transport message. */
const errorMessage = (error: unknown): string => {
    const body = (error as { response?: { data?: unknown } } | null)?.response?.data;
    if (body && typeof body === 'object' && typeof (body as { error?: unknown }).error === 'string') {
        return (body as { error: string }).error;
    }
    return error instanceof Error ? error.message : String(error);
};

@injectable()
export class TradingHoursAdminModelImpl implements TradingHoursAdminModel {
    readonly #disposableStack = new DisposableStack();
    readonly #state = signal<TradingHoursAdminState>(initialTradingHoursAdminState);
    readonly #abort = new AbortController();
    readonly #queryClient: ReactBindings['QueryClient'];
    readonly #store: ReactBindings['Store'];
    readonly #downloading = signal(false);

    readonly tab: ReadonlySignal<TradingHoursTab>;
    readonly status: QueryView<TradingHoursScheduleStatusView>;
    readonly versions: QueryView<TradingHoursVersionsView>;
    readonly overrides: QueryView<readonly TradingHoursRecordOverrideView[]>;
    readonly overrideRows: ReadonlySignal<readonly TradingHoursRecordOverrideView[]>;
    readonly history: QueryView<TradingHoursHistoryView>;
    readonly holidayHistory: QueryView<TradingHoursHolidayHistoryView>;
    readonly diff: QueryView<ScheduleDiff>;
    readonly unreachable: ReadonlySignal<boolean>;
    readonly health: ReadonlySignal<ServingHealth>;
    readonly timeline: ReadonlySignal<readonly TimelineEntry[]>;
    readonly busy: ReadonlySignal<boolean>;
    readonly versionsLimit: ReadonlySignal<number>;
    readonly versionRows: ReadonlySignal<readonly TradingHoursIngestionView[]>;
    readonly healthyVersions: ReadonlySignal<readonly TradingHoursIngestionView[]>;
    readonly selectedVersionId: ReadonlySignal<string | null>;
    readonly selectedVersion: ReadonlySignal<TradingHoursIngestionView | null>;
    readonly pendingAction: ReadonlySignal<PendingAction | null>;
    readonly recordForm: ReadonlySignal<RecordBlacklistForm>;
    readonly recordResult: ReadonlySignal<TradingHoursRecordBlacklistResult | null>;
    readonly recordFormIds: ReadonlySignal<readonly string[]>;
    readonly holidayDraft: ReadonlySignal<HolidayLookup>;
    readonly canLookupHoliday: ReadonlySignal<boolean>;
    readonly historyVersionId: ReadonlySignal<string | null>;
    readonly diffDraft: ReadonlySignal<DiffSelection>;
    readonly canRunDiff: ReadonlySignal<boolean>;
    readonly diffSelection: ReadonlySignal<DiffSelection | null>;

    constructor(
        @inject('QueryObserverModelFactory') queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'],
        @inject('QueryClient') queryClient: ReactBindings['QueryClient'],
        @inject('Store') store: ReactBindings['Store'],
    ) {
        this.#queryClient = queryClient;
        this.#store = store;

        this.tab = computed(() => this.#state.value.tab);
        this.versionsLimit = computed(() => this.#state.value.versionsLimit);
        this.pendingAction = computed(() => this.#state.value.pendingAction);
        this.recordForm = computed(() => this.#state.value.recordForm);
        this.recordResult = computed(() => this.#state.value.recordResult);
        this.recordFormIds = computed(() => parseIds(this.#state.value.recordForm.idsText));
        this.holidayDraft = computed(() => this.#state.value.holidayDraft);
        this.canLookupHoliday = computed(() => {
            const { date, market } = this.#state.value.holidayDraft;
            return /^\d{4}-\d{2}-\d{2}$/.test(date.trim()) && market.trim().length > 0;
        });
        this.historyVersionId = computed(() => this.#state.value.historyVersionId);
        this.diffDraft = computed(() => this.#state.value.diffDraft);
        this.diffSelection = computed(() => this.#state.value.diffSelection);
        this.canRunDiff = computed(() => {
            const { from, to } = this.#state.value.diffDraft;
            return from.length > 0 && to.length > 0 && from !== to;
        });

        const make = <T>(options: ReadonlySignal<QueryObserverOptions<T, Error, T>>): QueryView<T> =>
            this.#view(this.#disposableStack.use(queryObserverModelFactory<T, Error, T>(options)));

        this.status = make(computed(() => ({ ...getTradingHoursStatusQuery() })));
        this.versions = make(computed(() => ({ ...getTradingHoursVersionsQuery(this.#state.value.versionsLimit) })));
        const overridesView = make<TradingHoursRecordOverridesResult>(
            computed(() => ({ ...getTradingHoursOverridesQuery() })),
        );
        this.overrides = {
            data: computed(() => overridesView.data.value?.overrides),
            isLoading: overridesView.isLoading,
            isFetching: overridesView.isFetching,
            isError: overridesView.isError,
            error: overridesView.error,
            refetch: overridesView.refetch,
        };
        this.overrideRows = computed(() => this.overrides.data.value ?? EmptyArray);
        this.history = make(
            computed(() => ({
                ...getTradingHoursHistoryQuery(
                    this.#state.value.historyVersionId ?? undefined,
                    this.#state.value.historyLimit,
                ),
            })),
        );
        this.holidayHistory = make(
            computed(() => {
                const lookup = this.#state.value.holidayLookup;
                return {
                    ...getTradingHoursHolidayHistoryQuery(lookup ? holidayLookupParams(lookup) : { date: '', mic: '' }),
                    enabled: lookup !== null,
                };
            }),
        );
        this.diff = make(
            computed(() => {
                const selection = this.#state.value.diffSelection;
                return {
                    ...getTradingHoursDiffQuery(selection?.from ?? '', selection?.to ?? ''),
                    enabled: selection !== null,
                };
            }),
        );

        this.unreachable = computed(() => {
            const error = this.status.error.value;
            if (!error) return false;
            const status = (error as { status?: unknown }).status;
            // No HTTP status is a DNS or connection failure; a gateway error means the worker is down behind a proxy.
            return typeof status !== 'number' || UNREACHABLE_STATUSES.has(status);
        });
        this.health = computed<ServingHealth>(() => {
            const status = this.status.data.value;
            if (!status || status.effective === null) return 'none';
            if (status.pinned) {
                const age = status.pin?.ageSeconds ?? 0;
                return age >= STALE_PIN_SECONDS ? 'pinned-stale' : 'pinned';
            }
            return status.effectiveIsLatest ? 'latest' : 'behind';
        });
        this.timeline = computed<readonly TimelineEntry[]>(() => {
            const history = this.history.data.value;
            if (!history) return EmptyArray;
            const entries: TimelineEntry[] = [
                ...history.events.map((event): TimelineEntry => ({ kind: 'event', at: event.at, event })),
                ...history.effective.map((period): TimelineEntry => ({ kind: 'period', at: period.startedAt, period })),
            ];
            return entries.sort((a, b) => b.at.compareTo(a.at));
        });
        this.busy = computed(() => (this.#state.value.pendingAction?.busy ?? false) || this.#downloading.value);
        this.versionRows = computed(() => this.versions.data.value?.versions ?? EmptyArray);
        this.healthyVersions = computed(() =>
            this.versionRows.value.filter((version) => version.status === 'complete' && !version.blacklisted),
        );
        this.selectedVersionId = computed(() => this.#state.value.selectedVersionId);
        this.selectedVersion = computed(() => {
            const id = this.#state.value.selectedVersionId;
            if (id === null) return null;
            return this.versionRows.value.find((version) => version.id === id) ?? null;
        });

        this.#disposableStack.defer(() => this.#abort.abort());
    }

    #view<T>(observer: QueryObserverModel<T, Error, T>): QueryView<T> {
        return {
            data: computed(() => observer.signal.value.data),
            isLoading: computed(() => observer.signal.value.isLoading),
            isFetching: computed(() => observer.signal.value.isFetching),
            isError: computed(() => observer.signal.value.isError),
            error: computed(() => observer.signal.value.error),
            refetch: () => void observer.signal.peek().refetch(),
        };
    }

    #dispatch(action: TradingHoursAdminAction): void {
        const next = tradingHoursAdminReducer(this.#state.peek(), action);
        if (next !== this.#state.peek()) this.#state.value = next;
    }

    #toast(variant: 'success' | 'error', message: string): void {
        this.#store.dispatch({ type: 'toast::enqueue', toast: { message, group: 'default', variant } });
    }

    setTab(tab: TradingHoursTab): void {
        this.#dispatch(setTabAction(tab));
    }

    setVersionsLimit(limit: number): void {
        this.#dispatch(setVersionsLimitAction(limit));
    }

    selectVersion(ingestionId: string | null): void {
        this.#dispatch(selectVersionAction(ingestionId));
    }

    openConfirm(action: ConfirmableAction, reason = ''): void {
        this.#dispatch(openConfirmAction(action, reason));
    }

    openFreeze(preset: { blacklistCurrent?: boolean; pinTo?: string | null } = {}): void {
        this.#dispatch(openFreezeAction(preset, this.status.data.peek()?.effective?.id ?? null));
    }

    openUnfreeze(): void {
        this.#dispatch(openUnfreezeAction());
        void this.#loadUnfreezePreview(this.#state.peek().pendingAction);
    }

    /** `opened` is the dialog this preview belongs to; a late result for a closed or replaced dialog is dropped. */
    async #loadUnfreezePreview(opened: PendingAction | null): Promise<void> {
        const stillOpen = () => !this.#abort.signal.aborted && this.#state.peek().pendingAction === opened;
        try {
            const preview = await unfreeze(false);
            if (!stillOpen()) return;
            this.#dispatch(setUnfreezePreview(preview));
        } catch (error) {
            log.error('Loading the TradingHours unfreeze preview failed', { error });
            if (!stillOpen()) return;
            this.#dispatch(closePending());
            this.#toast(
                'error',
                t(
                    'admin::trading-hours::toast::unfreeze-preview-error',
                    'Could not preview the unfreeze: {{message}}',
                    {
                        message: errorMessage(error),
                    },
                ),
            );
        }
    }

    setPendingReason(reason: string): void {
        this.#dispatch(setPendingReasonAction(reason));
    }

    setFreezeOptions(options: { blacklistCurrent?: boolean; pinTo?: string | null }): void {
        const pending = this.#state.peek().pendingAction;
        // Blacklisting and pinning the same version is refused by the worker; drop such a target.
        const currentId = this.status.data.peek()?.effective?.id;
        const pinTo = options.pinTo !== undefined ? options.pinTo : pending?.kind === 'freeze' ? pending.pinTo : null;
        const blacklistCurrent =
            options.blacklistCurrent ?? (pending?.kind === 'freeze' ? pending.blacklistCurrent : true);
        const next = blacklistCurrent && pinTo !== null && pinTo === currentId ? { ...options, pinTo: null } : options;
        this.#dispatch(setFreezeOptionsAction(next));
    }

    cancelPending(): void {
        if (this.#state.peek().pendingAction?.busy) return;
        this.#dispatch(closePending());
    }

    async confirmPending(): Promise<void> {
        const pending = this.#state.peek().pendingAction;
        if (!pending || pending.busy) return;
        // An unfreeze is never applied blind: the preview must have arrived.
        if (pending.kind === 'unfreeze' && pending.preview === null) return;
        this.#dispatch(setPendingBusy(true));
        try {
            const message = await this.#runPending(pending);
            if (this.#abort.signal.aborted) return;
            this.#dispatch(closePending());
            this.#toast('success', message);
        } catch (error) {
            log.error(`TradingHours lever ${describePending(pending)} failed`, { error, pending });
            if (this.#abort.signal.aborted) return;
            this.#dispatch(setPendingBusy(false));
            this.#toast(
                'error',
                t('admin::trading-hours::toast::lever-error', '{{action}} failed: {{message}}', {
                    action: describePending(pending),
                    message: errorMessage(error),
                    interpolation: { escapeValue: false },
                }),
            );
        } finally {
            await invalidateTradingHoursAdminQueries(this.#queryClient);
        }
    }

    /**
     * The dialog names a specific current version (to blacklist, or to keep and pin). Ingestion can
     * advance while it is open, so re-read the status and refuse if the current version moved.
     */
    async #assertEffectiveUnchanged(effectiveId: string | null): Promise<void> {
        const fresh = await this.#queryClient.fetchQuery(getTradingHoursStatusQuery());
        const nowEffective = fresh.effective?.id ?? null;
        if (nowEffective !== effectiveId) {
            // Re-baseline the open dialog so a reviewed reconfirmation can succeed.
            if (!this.#abort.signal.aborted) this.#dispatch(setFreezeEffectiveId(nowEffective));
            throw new Error(
                t(
                    'admin::trading-hours::freeze::effective-moved',
                    'The effective version changed while the dialog was open ({{was}} → {{now}}). Review the new state and try again.',
                    { was: effectiveId ? shortId(effectiveId) : '—', now: nowEffective ? shortId(nowEffective) : '—' },
                ),
            );
        }
    }

    /**
     * The operator approved a specific pinned → becomes-effective pair. Re-preview before applying and
     * refuse if either side moved, showing the fresh preview so the next decision is informed.
     */
    async #assertUnfreezePreviewCurrent(pending: Extract<PendingAction, { kind: 'unfreeze' }>): Promise<void> {
        const approved = pending.preview;
        if (!approved) return;
        const fresh = await unfreeze(false);
        const same =
            fresh.pinned.id === approved.pinned.id &&
            (fresh.becomesEffective?.id ?? null) === (approved.becomesEffective?.id ?? null);
        if (same) return;
        // `pending` was captured before the busy flag flipped, so match the open dialog by kind.
        if (!this.#abort.signal.aborted && this.#state.peek().pendingAction?.kind === 'unfreeze') {
            this.#dispatch(setUnfreezePreview(fresh));
        }
        throw new Error(
            t(
                'admin::trading-hours::unfreeze::preview-moved',
                'The state changed while the dialog was open; the preview has been refreshed. Review it and confirm again.',
            ),
        );
    }

    /** Runs one lever and returns the success toast text. */
    async #runPending(pending: PendingAction): Promise<string> {
        const reason = (reasonOf: string) => (reasonOf.trim().length > 0 ? reasonOf.trim() : undefined);
        switch (pending.kind) {
            case 'freeze': {
                // Every freeze is reviewed against a specific current version: the one named in the dialog.
                await this.#assertEffectiveUnchanged(pending.effectiveId);
                // "Keep the current version" pins the version the operator saw, never whatever is current later.
                const pinTo = pending.pinTo ?? (pending.blacklistCurrent ? null : pending.effectiveId);
                if (!pending.blacklistCurrent && pinTo === null) {
                    throw new Error(
                        t(
                            'admin::trading-hours::freeze::no-current',
                            'No effective version to pin; pick a version explicitly.',
                        ),
                    );
                }
                await freeze({
                    blacklistCurrent: pending.blacklistCurrent,
                    ...(pinTo ? { pinTo } : {}),
                    ...(reason(pending.reason) ? { reason: reason(pending.reason) } : {}),
                });
                return t('admin::trading-hours::toast::freeze-success', 'Trading hours frozen on a pinned version.');
            }
            case 'unfreeze': {
                await this.#assertUnfreezePreviewCurrent(pending);
                await unfreeze(true);
                return t(
                    'admin::trading-hours::toast::unfreeze-success',
                    'Pin cleared; the latest healthy version is served.',
                );
            }
            case 'confirm': {
                const action = pending.action;
                switch (action.kind) {
                    case 'blacklist-version':
                        await setVersionsBlacklist({
                            ingestionIds: [action.ingestionId],
                            blacklisted: action.blacklisted,
                            reason: reason(pending.reason),
                        });
                        return action.blacklisted
                            ? t('admin::trading-hours::toast::blacklist-success', 'Version blacklisted.')
                            : t('admin::trading-hours::toast::unblacklist-success', 'Version restored.');
                    case 'release-override':
                        await releaseRecordOverride(action.overrideId, reason(pending.reason));
                        return t('admin::trading-hours::toast::release-success', 'Override released: {{label}}', {
                            label: action.label,
                            interpolation: { escapeValue: false },
                        });
                    case 'record-blacklist': {
                        const result = await setRecordsBlacklist({
                            collection: action.collection,
                            ids: [...action.ids],
                            blacklisted: action.blacklisted,
                            reason: reason(pending.reason),
                        });
                        if (!this.#abort.signal.aborted) {
                            this.#dispatch(setRecordResult(result));
                            this.#dispatch(setRecordFormAction({ idsText: '' }));
                        }
                        return t('admin::trading-hours::toast::record-success', '{{note}} ({{modified}} modified)', {
                            note: result.note,
                            modified: result.modified,
                            interpolation: { escapeValue: false },
                        });
                    }
                }
            }
        }
    }

    setRecordForm(form: Partial<RecordBlacklistForm>): void {
        this.#dispatch(setRecordFormAction(form));
    }

    submitRecordForm(): void {
        const { recordForm } = this.#state.peek();
        const ids = parseIds(recordForm.idsText);
        if (ids.length === 0) return;
        this.#dispatch(setRecordResult(null));
        this.openConfirm(
            {
                kind: 'record-blacklist',
                collection: recordForm.collection,
                ids,
                blacklisted: recordForm.blacklisted,
                label: t('admin::trading-hours::overrides::confirm-label', '{{count}} {{collection}} row(s)', {
                    count: ids.length,
                    collection: recordForm.collection,
                }),
            },
            recordForm.reason,
        );
    }

    setHolidayDraft(draft: Partial<HolidayLookup>): void {
        this.#dispatch(setHolidayDraftAction(draft));
    }

    lookupHoliday(): void {
        if (!this.canLookupHoliday.peek()) return;
        const before = this.#state.peek();
        this.#dispatch(submitHolidayLookup());
        // Same inputs as last time: the query key did not change, so retry explicitly.
        if (this.#state.peek() === before) this.holidayHistory.refetch();
    }

    setHistoryFilter(ingestionId: string | null): void {
        this.#dispatch(setHistoryFilterAction(ingestionId));
    }

    setDiffDraft(draft: Partial<DiffSelection>): void {
        this.#dispatch(setDiffDraftAction(draft));
    }

    runDiff(): void {
        if (!this.canRunDiff.peek()) return;
        const before = this.#state.peek();
        this.#dispatch(submitDiff());
        if (this.#state.peek() === before) this.diff.refetch();
    }

    async downloadArchive(ingestionId: string): Promise<void> {
        if (this.#downloading.peek()) return;
        this.#downloading.value = true;
        try {
            const blob = await downloadArchiveRequest(ingestionId);
            if (this.#abort.signal.aborted) return;
            triggerBlobDownload(blob, `trading-hours-${ingestionId}.zip`);
        } catch (error) {
            log.error(`Downloading TradingHours archive ${ingestionId} failed`, { error, ingestionId });
            if (this.#abort.signal.aborted) return;
            this.#toast(
                'error',
                t('admin::trading-hours::toast::download-error', 'Archive download failed: {{message}}', {
                    message: errorMessage(error),
                    interpolation: { escapeValue: false },
                }),
            );
        } finally {
            this.#downloading.value = false;
        }
    }

    async refreshAll(): Promise<void> {
        await invalidateTradingHoursAdminQueries(this.#queryClient);
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }
}

/** Short English label of a lever for logs and the error toast. */
export const describePending = (pending: PendingAction): string => {
    switch (pending.kind) {
        case 'freeze':
            return 'Freeze';
        case 'unfreeze':
            return 'Unfreeze';
        case 'confirm':
            switch (pending.action.kind) {
                case 'blacklist-version':
                    return pending.action.blacklisted ? 'Blacklist version' : 'Restore version';
                case 'release-override':
                    return 'Release override';
                case 'record-blacklist':
                    return pending.action.blacklisted ? 'Switch records off' : 'Switch records on';
            }
    }
};
